import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  serverTimestamp,
  arrayUnion,
  Timestamp,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import type { PetitionState, PetitionFilters } from './state'
import type {
  Petition,
  PetitionStatus,
  PetitionCategory,
  Priority,
  PetitionComment,
  RequirementType,
} from '@/types'
import { useRequirementsStore } from '@/views/Requirements/store'

export interface CreatePetitionData {
  organizationId: string
  projectId?: string
  title: string
  description: string
  extendedDescription?: string
  category: PetitionCategory
  priority: Priority
  attachments?: string[]
}

export interface UpdatePetitionData {
  title?: string
  description?: string
  extendedDescription?: string
  category?: PetitionCategory
  priority?: Priority
  status?: PetitionStatus
  estimatedHours?: number
  estimatedCost?: number
  estimatedDelivery?: Date
  rejectionReason?: string
  attachments?: string[]
}

const convertTimestamps = (data: Record<string, unknown>): Petition => {
  const petition = { ...data } as Record<string, unknown>

  const timestampFields = [
    'createdAt',
    'updatedAt',
    'approvedAt',
    'rejectedAt',
    'convertedAt',
    'estimatedDelivery',
  ]

  timestampFields.forEach((field) => {
    if (petition[field] instanceof Timestamp) {
      petition[field] = (petition[field] as Timestamp).toDate()
    }
  })

  // Convert comments timestamps
  if (Array.isArray(petition.comments)) {
    petition.comments = petition.comments.map((comment: Record<string, unknown>) => ({
      ...comment,
      createdAt: comment.createdAt instanceof Timestamp
        ? (comment.createdAt as Timestamp).toDate()
        : comment.createdAt,
    }))
  }

  return petition as unknown as Petition
}

export const createActions = (state: PetitionState) => {
  const authStore = useAuthStore()
  const { logActivity } = useActivityLog()
  const notificationsStore = useNotificationsStore()
  let globalUnsubscribe: Unsubscribe | null = null

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return

    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []
    const role = authStore.user?.role

    let q = query(
      collection(db, 'petitions'),
      ...baseConstraints,
      orderBy('createdAt', 'desc')
    )

    if (
      (role === 'client' || role === 'member') &&
      authStore.user?.organizationId
    ) {
      q = query(
        collection(db, 'petitions'),
        ...baseConstraints,
        where('organizationId', '==', authStore.user.organizationId),
        orderBy('createdAt', 'desc')
      )
    }

    state.isLoading = true

    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      let petitions = snapshot.docs.map((docSnap) => ({
        ...convertTimestamps(docSnap.data()),
        id: docSnap.id,
        tenantId: docSnap.data().tenantId,
      }))

      if (role === 'operator') {
        const orgIds = new Set(
          authStore.user?.assignedOrganizationIds || []
        )
        petitions = petitions.filter((p) => orgIds.has(p.organizationId))
      }

      state.petitions = petitions
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchPetitions = async (organizationId?: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []

      let q = query(collection(db, 'petitions'), ...baseConstraints, orderBy('createdAt', 'desc'))

      // Filter by organization if provided or if user is client
      const orgId = organizationId || state.filters.organizationId
      if (orgId) {
        q = query(
          collection(db, 'petitions'),
          ...baseConstraints,
          where('organizationId', '==', orgId),
          orderBy('createdAt', 'desc')
        )
      }

      // If user is a client/member, only show their organization's petitions
      if ((authStore.user?.role === 'client' || authStore.user?.role === 'member') && authStore.user.organizationId) {
        q = query(
          collection(db, 'petitions'),
          ...baseConstraints,
          where('organizationId', '==', authStore.user.organizationId),
          orderBy('createdAt', 'desc')
        )
      } else if (authStore.user?.role === 'operator') {
        const orgIds = authStore.user.assignedOrganizationIds || []
        if (orgIds.length === 0) {
          state.petitions = []
          state.isLoading = false
          return
        }
        // Fetch in batches of 30
        const allPetitions: typeof state.petitions = []
        for (let i = 0; i < orgIds.length; i += 30) {
          const batch = orgIds.slice(i, i + 30)
          const batchQ = query(
            collection(db, 'petitions'),
            ...baseConstraints,
            where('organizationId', 'in', batch),
            orderBy('createdAt', 'desc')
          )
          const snap = await getDocs(batchQ)
          allPetitions.push(...snap.docs.map((docSnap) => ({
            ...convertTimestamps(docSnap.data()),
            id: docSnap.id,
            tenantId: docSnap.data().tenantId,
          })))
        }
        state.petitions = allPetitions.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        state.isLoading = false
        return
      }

      const snapshot = await getDocs(q)
      state.petitions = snapshot.docs.map((docSnap) => ({
        ...convertTimestamps(docSnap.data()),
        id: docSnap.id,
        tenantId: docSnap.data().tenantId,
      }))
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching petitions'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const fetchPetitionById = async (id: string): Promise<Petition | null> => {
    try {
      const docRef = doc(db, 'petitions', id)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      return {
        ...convertTimestamps(docSnap.data()),
        id: docSnap.id,
        tenantId: docSnap.data().tenantId,
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching petition'
      throw error
    }
  }

  const createPetition = async (data: CreatePetitionData): Promise<string> => {
    const userId = authStore.userId
    if (!userId) throw new Error('User not authenticated')

    try {
      const petitionData = {
        ...getTenantData(),
        ...data,
        status: 'requested' as PetitionStatus,
        comments: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        updatedBy: userId,
      }

      const docRef = await addDoc(collection(db, 'petitions'), petitionData)

      await logActivity(
        'petition',
        docRef.id,
        'created',
        `Se creo la peticion "${data.title}"`
      )

      // Notify admins and operators about new petition
      await notificationsStore.notifyAdminsAndOperators(
        'petition_created',
        'Nueva petición creada',
        `Se ha creado una nueva petición: "${data.title}"`,
        'petition',
        docRef.id
      )

      await fetchPetitions()
      return docRef.id
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error creating petition'
      throw error
    }
  }

  const updatePetition = async (
    id: string,
    data: UpdatePetitionData
  ): Promise<void> => {
    const userId = authStore.userId
    if (!userId) throw new Error('User not authenticated')

    try {
      const docRef = doc(db, 'petitions', id)
      const updateData: Record<string, unknown> = {
        ...data,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      }

      // Handle date conversion
      if (data.estimatedDelivery) {
        updateData.estimatedDelivery = Timestamp.fromDate(data.estimatedDelivery)
      }

      await updateDoc(docRef, updateData)

      await logActivity(
        'petition',
        id,
        'updated',
        'Se actualizo la peticion'
      )

      await fetchPetitions()
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error updating petition'
      throw error
    }
  }

  const updatePetitionStatus = async (
    id: string,
    newStatus: PetitionStatus,
    reason?: string
  ): Promise<void> => {
    const userId = authStore.userId
    if (!userId) throw new Error('User not authenticated')

    try {
      const docRef = doc(db, 'petitions', id)
      const updateData: Record<string, unknown> = {
        status: newStatus,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      }

      // Set approval/rejection timestamps
      if (newStatus === 'approved') {
        updateData.approvedAt = serverTimestamp()
        updateData.approvedBy = userId
      } else if (newStatus === 'rejected') {
        updateData.rejectedAt = serverTimestamp()
        updateData.rejectedBy = userId
        if (reason) {
          updateData.rejectionReason = reason
        }
      }

      await updateDoc(docRef, updateData)

      await logActivity(
        'petition',
        id,
        'status_changed',
        `Se cambio el estado de la peticion a "${newStatus}"`
      )

      await fetchPetitions()
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error updating petition status'
      throw error
    }
  }

  const addComment = async (
    petitionId: string,
    content: string,
    isInternal = false
  ): Promise<void> => {
    const userId = authStore.userId
    const userName = authStore.user?.displayName || 'Usuario'
    if (!userId) throw new Error('User not authenticated')

    try {
      const docRef = doc(db, 'petitions', petitionId)
      const comment: Omit<PetitionComment, 'createdAt'> & { createdAt: ReturnType<typeof serverTimestamp> } = {
        id: `comment-${Date.now()}`,
        content,
        isInternal,
        createdAt: serverTimestamp(),
        createdBy: userId,
        createdByName: userName,
      }

      await updateDoc(docRef, {
        comments: arrayUnion(comment),
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })

      await logActivity(
        'petition',
        petitionId,
        'commented',
        isInternal ? 'Se agrego un comentario interno' : 'Se agrego un comentario'
      )

      await fetchPetitions()
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error adding comment'
      throw error
    }
  }

  const deletePetition = async (id: string): Promise<void> => {
    try {
      // Get petition for logging
      const petition = state.petitions.find((p) => p.id === id)

      await deleteDoc(doc(db, 'petitions', id))

      await logActivity(
        'petition',
        id,
        'deleted',
        `Se elimino la peticion "${petition?.title || id}"`
      )

      state.petitions = state.petitions.filter((p) => p.id !== id)
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error deleting petition'
      throw error
    }
  }

  const setFilters = (filters: Partial<PetitionFilters>): void => {
    Object.assign(state.filters, filters)
  }

  const resetFilters = (): void => {
    state.filters = {
      search: '',
      status: '',
      category: '',
      priority: '',
      projectId: '',
      organizationId: '',
    }
  }

  // Mark petition as converted (called after transforming to requirement)
  const markAsConverted = async (
    petitionId: string,
    requirementId: string
  ): Promise<void> => {
    const userId = authStore.userId
    if (!userId) throw new Error('User not authenticated')

    try {
      const docRef = doc(db, 'petitions', petitionId)
      await updateDoc(docRef, {
        status: 'converted' as PetitionStatus,
        convertedToRequirementId: requirementId,
        convertedAt: serverTimestamp(),
        convertedBy: userId,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })

      await logActivity(
        'petition',
        petitionId,
        'status_changed',
        `Se convirtio la peticion en requerimiento ${requirementId}`
      )

      await fetchPetitions()
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error marking petition as converted'
      throw error
    }
  }

  // Transform an approved petition into a requirement
  const transformPetitionToRequirement = async (
    petitionId: string,
    projectId: string,
    requirementType: RequirementType = 'task',
    additionalData?: {
      epicId?: string
      storyPoints?: number
      assignedTo?: string
    }
  ): Promise<string> => {
    const userId = authStore.userId
    if (!userId) throw new Error('User not authenticated')

    try {
      // Get the petition
      const petition = await fetchPetitionById(petitionId)
      if (!petition) {
        throw new Error('Peticion no encontrada')
      }

      // Verify petition is approved
      if (petition.status !== 'approved') {
        throw new Error('Solo se pueden transformar peticiones aprobadas')
      }

      // Map petition category to requirement type (can be overridden)
      const categoryToTypeMap: Record<PetitionCategory, RequirementType> = {
        new_feature: 'story',
        improvement: 'task',
        bug_report: 'bug',
        change_request: 'task',
        support: 'task',
        other: 'task',
      }
      const finalType = requirementType || categoryToTypeMap[petition.category]

      // Create the requirement
      const requirementsStore = useRequirementsStore()
      const newRequirement = await requirementsStore.createRequirement({
        projectId,
        organizationId: petition.organizationId,
        title: petition.title,
        description: petition.description,
        type: finalType,
        priority: petition.priority,
        definitionStatus: 'approved', // Auto-approved since petition was already approved
        developmentStatus: 'backlog',
        estimatedHours: petition.estimatedHours,
        epicId: additionalData?.epicId,
        storyPoints: additionalData?.storyPoints,
        assignedTo: additionalData?.assignedTo,
        clientNotes: `Convertido desde peticion: ${petition.title}`,
      })

      // Mark petition as converted
      await markAsConverted(petitionId, newRequirement.id)

      return newRequirement.id
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error transforming petition'
      throw error
    }
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchPetitions,
    fetchPetitionById,
    createPetition,
    updatePetition,
    updatePetitionStatus,
    addComment,
    deletePetition,
    setFilters,
    resetFilters,
    markAsConverted,
    transformPetitionToRequirement,
  }
}
